import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import customerOrderService from '../services/customerOrderService';
import CreditApplicationForm from '../components/CreditApplicationForm';

const BASE = process.env.REACT_APP_API_URL || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

const EN = 'en';
const ES = 'es';

function tx(enStr, esStr, lang) {
  return lang === ES ? esStr : enStr;
}

const STATUS_CONFIG = {
  pending:       { en: 'Pending',       es: 'Pendiente',    color: '#f59e0b', bg: '#fffbeb' },
  confirmed:     { en: 'Confirmed',     es: 'Confirmado',   color: '#3b82f6', bg: '#eff6ff' },
  in_production: { en: 'In Production', es: 'En Producción',color: '#8b5cf6', bg: '#f5f3ff' },
  ready:         { en: 'Ready',         es: 'Listo',        color: '#10b981', bg: '#ecfdf5' },
  shipped:       { en: 'Shipped',       es: 'Enviado',      color: '#6b7280', bg: '#f9fafb' },
  invoiced:      { en: 'Invoiced',      es: 'Facturado',    color: '#1f2937', bg: '#f3f4f6' },
  cancelled:     { en: 'Cancelled',     es: 'Cancelado',    color: '#ef4444', bg: '#fef2f2' },
};

function fmt(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function StatusPill({ status, lang }) {
  const cfg = STATUS_CONFIG[status] || { en: status, es: status, color: '#6b7280', bg: '#f9fafb' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 20,
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.03em',
      color: cfg.color,
      background: cfg.bg,
      border: `1px solid ${cfg.color}40`,
    }}>
      {tx(cfg.en, cfg.es, lang)}
    </span>
  );
}

export default function CustomerOrderPortalPage() {
  const { token } = useParams();
  const [lang, setLang] = useState(EN);
  const [portal, setPortal] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [tab, setTab] = useState('new');
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  // ── Credit application gate ──────────────────────────────────────────────
  const [creditAppThankYou, setCreditAppThankYou] = useState(false);
  const [creditAppDismissed, setCreditAppDismissed] = useState(false);

  // ── Push notification state ──────────────────────────────────────────────
  const pushSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState(null);

  // Check on mount if this device is already subscribed
  const checkPushState = useCallback(async () => {
    if (!pushSupported) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      setPushSubscribed(!!sub);
    } catch { /* ignore */ }
  }, [pushSupported]);

  const handlePushSubscribe = async () => {
    if (!pushSupported) return;
    setPushBusy(true);
    setPushError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setPushError(tx(
          'Notification permission denied. Please allow notifications in your browser settings.',
          'Permiso de notificaciones denegado. Habilítalas en la configuración del navegador.',
          lang
        ));
        return;
      }

      // Register SW
      let reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) reg = await navigator.serviceWorker.register('/sw.js');

      // Get VAPID key
      const res = await fetch(`${BASE}/api/push/vapid-public-key`);
      const { publicKey } = await res.json();
      if (!publicKey) throw new Error('VAPID key unavailable');

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      const json = sub.toJSON();
      await fetch(`${BASE}/api/customer-orders/portal/${token}/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys, userAgent: navigator.userAgent }),
      });

      setPushSubscribed(true);
    } catch (err) {
      setPushError(tx('Could not enable notifications. Try again.', 'No se pudieron activar las notificaciones. Intenta de nuevo.', lang));
      console.warn('[Push] subscribe error:', err.message);
    } finally {
      setPushBusy(false);
    }
  };

  const handlePushUnsubscribe = async () => {
    setPushBusy(true);
    setPushError(null);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const endpoint = sub.toJSON().endpoint;
          await sub.unsubscribe();
          await fetch(`${BASE}/api/customer-orders/portal/${token}/push/subscribe`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ endpoint }),
          });
        }
      }
      setPushSubscribed(false);
    } catch (err) {
      console.warn('[Push] unsubscribe error:', err.message);
    } finally {
      setPushBusy(false);
    }
  };

  const toggleOrder = (id) => setExpandedOrders(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const [error, setError] = useState('');

  const emptyForm = { requestedDate: '', customerNotes: '', items: [] };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  // Check push state once portal loads
  useEffect(() => { checkPushState(); }, [checkPushState]);

  // Register SW and send this order's token so the dynamic manifest (manifest-order.json) has
  // the right start_url — otherwise "Add to Home Screen" falls back to the login page, since a
  // static manifest.json can't carry a per-customer token. Registered proactively on every visit
  // (not only when the user enables push) since Add to Home Screen is a separate action that can
  // happen first. See sw.js and index.html for the other two pieces of this.
  useEffect(() => {
    if (!token || !('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js').then(reg => {
      const send = () => (reg.active || reg.installing || reg.waiting)
        ?.postMessage({ type: 'SET_PORTAL_TOKEN', token });
      if (reg.active) { send(); return; }
      const sw = reg.installing || reg.waiting;
      if (sw) sw.addEventListener('statechange', e => { if (e.target.state === 'activated') send(); });
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        const data = await customerOrderService.getPortal(token);
        setPortal(data);
        setLang(data.customer?.portalLanguage || EN);
        if (data.priceList?.Items?.length) {
          setForm(f => ({
            ...f,
            items: data.priceList.Items.map(item => ({
              priceListItemId: item.id,
              productId: item.productId,
              productName: item.Product?.name || item.presentationLabel,
              presentationLabel: item.presentationLabel,
              unitsPerCase: item.unitsPerCase,
              unitPriceSnapshot: item.pricePerCase,
              quantity: '',
              quantityUnit: item.unitsPerCase > 0 ? 'cases' : 'units',
            })),
          }));
        }
      } catch {
        setError('Portal not found or no longer active.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await customerOrderService.getPortalOrders(token);
      setOrders(data);
    } catch {
      setOrders([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    if (newTab === 'history') loadHistory();
  };

  const setQty = (idx, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], quantity: value };
      return { ...f, items };
    });
  };

  const setUnit = (idx, unit) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], quantityUnit: unit };
      return { ...f, items };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const filled = form.items.filter(i => i.quantity && Number(i.quantity) > 0);
    if (!filled.length) {
      return setError(tx('Enter at least one quantity.', 'Ingresa al menos una cantidad.', lang));
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await customerOrderService.submitPortalOrder(token, {
        requestedDate: form.requestedDate || null,
        customerNotes: form.customerNotes,
        items: filled.map(i => ({
          priceListItemId: i.priceListItemId || null,
          productId: i.productId || null,
          presentationLabel: i.presentationLabel,
          unitsPerCase: i.unitsPerCase || null,
          unitPriceSnapshot: (() => {
            const ppc = Number(i.unitPriceSnapshot);
            const upc = Number(i.unitsPerCase);
            if (i.quantityUnit === 'units') {
              if (!(ppc > 0)) return null;
              return upc > 0 ? Math.round((ppc / upc) * 10000) / 10000 : ppc;
            }
            return ppc || null;
          })(),
          quantity: Number(i.quantity),
          quantityUnit: i.quantityUnit || 'cases',
        })),
      });
      setSubmitted(result.orderNumber);
      setForm(emptyForm);
    } catch {
      setError(tx('Failed to submit order. Please try again.', 'Error al enviar el pedido. Intenta de nuevo.', lang));
    } finally {
      setSubmitting(false);
    }
  };

  // Live order total — cases use pricePerCase; units use pricePerCase / unitsPerCase
  // If unitsPerCase is not set, treat pricePerCase as the per-unit price (individual products)
  const orderTotal = form.items.reduce((sum, item) => {
    const qty = Number(item.quantity);
    const pricePerCase = Number(item.unitPriceSnapshot);
    if (!(qty > 0) || !(pricePerCase > 0)) return sum;
    if (item.quantityUnit === 'units') {
      const upc = Number(item.unitsPerCase);
      const unitPrice = upc > 0 ? pricePerCase / upc : pricePerCase;
      return sum + qty * unitPrice;
    }
    return sum + qty * pricePerCase;
  }, 0);

  const filledCount = form.items.filter(i => Number(i.quantity) > 0).length;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  if (error && !portal) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center px-4">
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔗</div>
          <h5 className="text-danger mb-2">Portal not found</h5>
          <p className="text-muted small">This order link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  const { customer, priceList } = portal;

  // Blocks the whole portal (except this gate itself) until a required credit application is
  // submitted. Checked server-side too (POST /portal/:token/orders 403s) — this is UX, not the
  // actual security boundary. See docs/CREDIT_APPLICATION_MODULE.md.
  const creditAppGateActive = portal.creditApp?.required && !portal.creditApp?.submitted && !creditAppDismissed;

  if (creditAppGateActive) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 20px' }}>
          <div style={{ maxWidth: 640, margin: '0 auto' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>{customer.name}</div>
          </div>
        </div>
        {creditAppThankYou ? (
          <div style={{ maxWidth: 640, margin: '0 auto', padding: '48px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h5 style={{ fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              {tx('Thank you!', '¡Gracias!', lang)}
            </h5>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: 20 }}>
              {tx('Your application has been received.', 'Su solicitud ha sido recibida.', lang)}
            </p>
            <button
              onClick={() => setCreditAppDismissed(true)}
              style={{
                padding: '10px 24px', fontSize: '0.9rem', fontWeight: 700, color: '#fff',
                background: '#3b82f6', border: 'none', borderRadius: 8, cursor: 'pointer',
              }}
            >
              {tx('Continue to Order Portal', 'Continuar al Portal de Pedidos', lang)}
            </button>
          </div>
        ) : (
          <CreditApplicationForm
            token={token}
            lang={lang}
            customerName={customer.name}
            onSubmitted={() => setCreditAppThankYou(true)}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Top bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 20px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>{customer.name}</div>
            {customer.address && (
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 1 }}>{customer.address}</div>
            )}
            {customer.phone && (
              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                <a href={`tel:${customer.phone}`} style={{ color: '#6b7280', textDecoration: 'none' }}>{customer.phone}</a>
              </div>
            )}
            {customer.email && (
              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                <a href={`mailto:${customer.email}`} style={{ color: '#6b7280', textDecoration: 'none' }}>{customer.email}</a>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {[EN, ES].map(l => (
              <button key={l} onClick={() => setLang(l)}
                style={{
                  padding: '4px 12px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                  border: lang === l ? '1.5px solid #3b82f6' : '1.5px solid #e5e7eb',
                  background: lang === l ? '#eff6ff' : '#fff',
                  color: lang === l ? '#3b82f6' : '#6b7280',
                }}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px 64px' }}>

        {/* Page title */}
        <h5 style={{ fontWeight: 700, color: '#111827', marginBottom: 20 }}>
          {tx('Order Portal', 'Portal de Pedidos', lang)}
        </h5>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: 24 }}>
          {[
            { key: 'new', en: 'Place Order', es: 'Hacer Pedido' },
            { key: 'history', en: 'My Orders', es: 'Mis Pedidos' },
          ].map(t => (
            <button key={t.key} onClick={() => handleTabChange(t.key)}
              style={{
                padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '0.9rem',
                color: tab === t.key ? '#3b82f6' : '#6b7280',
                borderBottom: tab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
                marginBottom: -2,
              }}>
              {tx(t.en, t.es, lang)}
            </button>
          ))}
        </div>

        {/* ── Place Order Tab ── */}
        {tab === 'new' && (
          <>
            {submitted ? (
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #d1fae5', padding: '32px 24px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <h5 style={{ fontWeight: 700, color: '#065f46' }}>{tx('Order Submitted!', '¡Pedido Enviado!', lang)}</h5>
                <p style={{ color: '#6b7280', marginBottom: 4 }}>{tx('Your order number is:', 'Tu número de pedido es:', lang)}</p>
                <code style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>{submitted}</code>
                <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: 12, marginBottom: 20 }}>
                  {tx('We will contact you to confirm your order.', 'Te contactaremos para confirmar tu pedido.', lang)}
                </p>
                <button onClick={() => setSubmitted(null)}
                  style={{ padding: '10px 24px', borderRadius: 8, border: '1.5px solid #10b981', background: '#fff', color: '#10b981', fontWeight: 600, cursor: 'pointer' }}>
                  {tx('Place Another Order', 'Hacer Otro Pedido', lang)}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#b91c1c', fontSize: '0.875rem' }}>
                    {error}
                  </div>
                )}

                {/* Delivery date */}
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '16px 18px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#374151', marginBottom: 8 }}>
                    {tx('Requested Delivery Date', 'Fecha de Entrega Solicitada', lang)}
                    <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>({tx('optional', 'opcional', lang)})</span>
                  </label>
                  <input type="date" className="form-control form-control-sm" style={{ maxWidth: 200 }}
                    value={form.requestedDate}
                    onChange={e => setForm(f => ({ ...f, requestedDate: e.target.value }))} />
                </div>

                {/* Order total summary — shown as soon as any qty is entered */}
                {orderTotal > 0 && (
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 18px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: '#1d4ed8', fontSize: '0.9rem' }}>
                      {tx('Order Total', 'Total del Pedido', lang)}
                      <span style={{ fontWeight: 400, color: '#60a5fa', fontSize: '0.8rem', marginLeft: 8 }}>
                        ({filledCount} {tx('item(s)', 'artículo(s)', lang)})
                      </span>
                    </span>
                    <span style={{ fontWeight: 700, fontSize: '1.15rem', color: '#1d4ed8' }}>{fmt(orderTotal)}</span>
                  </div>
                )}

                {/* Products */}
                {priceList?.Items?.length > 0 ? (
                  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <div style={{ padding: '12px 18px', borderBottom: '1px solid #f3f4f6', background: '#f9fafb' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>
                        {tx('Products', 'Productos', lang)}
                      </span>
                    </div>
                    {priceList.Items.map((item, idx) => {
                      const formItem = form.items[idx] || {};
                      const qty = Number(formItem.quantity);
                      const upc = Number(item.unitsPerCase);
                      const hasCaseOption = upc > 0;
                      const unit = formItem.quantityUnit || (hasCaseOption ? 'cases' : 'units');
                      const pricePerCase = Number(item.pricePerCase);
                      // If unitsPerCase not set, treat pricePerCase as the per-unit price
                      const pricePerUnit = pricePerCase > 0 ? (hasCaseOption ? pricePerCase / upc : pricePerCase) : null;
                      const activePrice = unit === 'units' ? pricePerUnit : (pricePerCase || null);
                      const lineTotal = qty > 0 && activePrice ? qty * activePrice : null;
                      return (
                        <div key={item.id} style={{
                          padding: '14px 18px',
                          borderBottom: idx < priceList.Items.length - 1 ? '1px solid #f3f4f6' : 'none',
                          display: 'flex', alignItems: 'center', gap: 12,
                          background: qty > 0 ? '#f0f9ff' : '#fff',
                          transition: 'background 0.15s',
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
                              {item.Product?.name || item.presentationLabel}
                            </div>
                            <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 2 }}>
                              <span>{unit === 'units' ? tx('Individuals', 'Individuales', lang) : tx('Cases', 'Cajas', lang)}</span>
                              {activePrice && <span style={{ margin: '0 4px' }}>·</span>}
                              {activePrice && (
                                <span>
                                  {fmt(activePrice)}{unit === 'units' ? tx('/un', '/un', lang) : tx('/cs', '/cj', lang)}
                                </span>
                              )}
                              {unit === 'cases' && hasCaseOption && (
                                <span style={{ marginLeft: 4 }}>· {upc} {tx('units/case', 'un/caja', lang)}</span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                            {lineTotal !== null && (
                              <span style={{ fontWeight: 600, color: '#1d4ed8', fontSize: '0.9rem', minWidth: 64, textAlign: 'right' }}>
                                {fmt(lineTotal)}
                              </span>
                            )}
                            <div>
                              <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: qty > 0 ? '2px solid #3b82f6' : '1.5px solid #d1d5db' }}>
                                <input
                                  type="number"
                                  min="0" step="1"
                                  style={{
                                    width: 60, textAlign: 'center', padding: '6px 8px',
                                    border: 'none', borderRight: '1px solid #e5e7eb',
                                    fontSize: '0.95rem', fontWeight: qty > 0 ? 700 : 400,
                                    outline: 'none', color: '#111827',
                                  }}
                                  placeholder="0"
                                  value={formItem.quantity || ''}
                                  onChange={e => setQty(idx, e.target.value)}
                                />
                                <button type="button"
                                  onClick={() => hasCaseOption && setUnit(idx, 'cases')}
                                  disabled={!hasCaseOption}
                                  title={!hasCaseOption ? tx('Case ordering not available for this item', 'Pedido por caja no disponible para este artículo', lang) : undefined}
                                  style={{
                                    padding: '6px 7px', border: 'none', fontSize: '0.72rem', fontWeight: 600,
                                    cursor: hasCaseOption ? 'pointer' : 'not-allowed',
                                    background: !hasCaseOption ? '#f3f4f6' : unit === 'cases' ? '#3b82f6' : '#f9fafb',
                                    color: !hasCaseOption ? '#d1d5db' : unit === 'cases' ? '#fff' : '#6b7280',
                                  }}>
                                  {tx('cs', 'cj', lang)}
                                </button>
                                <button type="button"
                                  onClick={() => setUnit(idx, 'units')}
                                  style={{
                                    padding: '6px 7px', border: 'none', borderLeft: '1px solid #e5e7eb', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                                    background: unit === 'units' ? '#3b82f6' : '#f9fafb',
                                    color: unit === 'units' ? '#fff' : '#6b7280',
                                  }}>
                                  {tx('un', 'un', lang)}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem', marginBottom: 16 }}>
                    {tx('No price list configured. Contact us to set up your product list.', 'Sin lista de precios configurada. Contáctanos para configurar tu lista de productos.', lang)}
                  </div>
                )}

                {/* Notes */}
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '16px 18px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#374151', marginBottom: 8 }}>
                    {tx('Notes', 'Notas', lang)}
                    <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>({tx('optional', 'opcional', lang)})</span>
                  </label>
                  <textarea
                    className="form-control form-control-sm" rows={3}
                    placeholder={tx('Special instructions, delivery notes...', 'Instrucciones especiales, notas de entrega...', lang)}
                    value={form.customerNotes}
                    onChange={e => setForm(f => ({ ...f, customerNotes: e.target.value }))}
                    style={{ resize: 'none' }}
                  />
                </div>

                <button type="submit"
                  disabled={submitting || !priceList?.Items?.length}
                  style={{
                    width: '100%', padding: '13px', borderRadius: 10, border: 'none',
                    background: submitting || !priceList?.Items?.length ? '#93c5fd' : '#3b82f6',
                    color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: submitting ? 'wait' : 'pointer',
                    boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                  }}>
                  {submitting
                    ? <><span className="spinner-border spinner-border-sm me-2" style={{ width: 16, height: 16, borderWidth: 2 }} />{tx('Submitting...', 'Enviando...', lang)}</>
                    : orderTotal > 0
                      ? `${tx('Submit Order', 'Enviar Pedido', lang)} — ${fmt(orderTotal)}`
                      : tx('Submit Order', 'Enviar Pedido', lang)
                  }
                </button>
              </form>
            )}
          </>
        )}

        {/* ── My Orders Tab ── */}
        {tab === 'history' && (
          historyLoading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
          ) : orders.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '48px 0', fontSize: '0.9rem' }}>
              {tx('No orders yet.', 'Sin pedidos aún.', lang)}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {orders.map(ord => {
                const isExpanded = expandedOrders.has(ord.id);
                const histTotal = (ord.Items || []).reduce((sum, item) => {
                  const qty = Number(item.quantity);
                  const price = Number(item.unitPriceSnapshot);
                  return sum + (qty > 0 && price > 0 ? qty * price : 0);
                }, 0);
                const submittedDate = ord.createdAt
                  ? new Date(ord.createdAt).toLocaleDateString()
                  : null;

                return (
                  <div key={ord.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    {/* Clickable header — always visible */}
                    <button
                      onClick={() => toggleOrder(ord.id)}
                      style={{
                        width: '100%', background: '#f9fafb', border: 'none', cursor: 'pointer',
                        padding: '12px 16px', borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <code style={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>{ord.orderNumber}</code>
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                          {submittedDate && `${tx('Submitted', 'Enviado', lang)} ${submittedDate}`}
                          {ord.requestedDate && ` · 📅 ${ord.requestedDate}`}
                          {histTotal > 0 && ` · ${fmt(histTotal)}`}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                        <StatusPill status={ord.status} lang={lang} />
                        <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </button>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <>
                        {/* Items */}
                        {ord.Items?.length > 0 && (
                          <div style={{ padding: '12px 16px' }}>
                            {ord.Items.map((item, i) => {
                              const qty = Number(item.quantity);
                              const price = Number(item.unitPriceSnapshot);
                              const lineTotal = qty > 0 && price > 0 ? qty * price : null;
                              const productName = item.Product?.name || item.presentationLabel || '—';
                              const showLabel = item.presentationLabel && item.presentationLabel !== item.Product?.name;
                              return (
                                <div key={i} style={{
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '7px 0',
                                  borderBottom: i < ord.Items.length - 1 ? '1px solid #f3f4f6' : 'none',
                                }}>
                                  <div>
                                    <span style={{ fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>{productName}</span>
                                    {showLabel && (
                                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: 6 }}>({item.presentationLabel})</span>
                                    )}
                                  </div>
                                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                                    <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                                      ×{item.quantity} {item.quantityUnit === 'units' ? tx('un', 'un', lang) : tx('cs', 'cj', lang)}
                                    </span>
                                    {lineTotal !== null && (
                                      <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1d4ed8', marginLeft: 10 }}>{fmt(lineTotal)}</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Notes from customer */}
                        {ord.customerNotes && (
                          <div style={{ padding: '0 16px 12px' }}>
                            <div style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 12px', fontSize: '0.825rem', color: '#6b7280', fontStyle: 'italic' }}>
                              💬 {ord.customerNotes}
                            </div>
                          </div>
                        )}

                        {/* Order total */}
                        {histTotal > 0 && (
                          <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 500 }}>
                              {tx('Order Total', 'Total del Pedido', lang)}
                            </span>
                            <span style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>{fmt(histTotal)}</span>
                          </div>
                        )}

                        {/* QBO Invoice — only show public payment URL to customers, never the admin URL */}
                        {ord.qbInvoiceNumber && (
                          <div style={{
                            padding: '12px 16px',
                            borderTop: '1px solid #d1fae5',
                            background: '#f0fdf4',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 12,
                          }}>
                            <div>
                              <div style={{ fontSize: '0.7rem', color: '#065f46', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                                {tx('Invoice', 'Factura', lang)}
                              </div>
                              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#065f46' }}>
                                #{ord.qbInvoiceNumber}
                              </div>
                            </div>
                            {/* Only show the button if the URL is a public payment link (not the admin edit page) */}
                            {ord.qbInvoiceUrl && !ord.qbInvoiceUrl.includes('/app/invoice?') ? (
                              <a
                                href={ord.qbInvoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  padding: '8px 16px', borderRadius: 8,
                                  background: '#10b981', color: '#fff',
                                  fontSize: '0.85rem', fontWeight: 600,
                                  textDecoration: 'none',
                                  whiteSpace: 'nowrap',
                                  boxShadow: '0 1px 4px rgba(16,185,129,0.25)',
                                }}
                              >
                                {tx('View & Pay', 'Ver y Pagar', lang)} →
                              </a>
                            ) : (
                              <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                                {tx('Payment link coming soon', 'Enlace de pago próximamente', lang)}
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
        {/* ── Get notified card ── */}
        {pushSupported && (
          <div style={{
            marginTop: 32,
            background: pushSubscribed ? '#f0fdf4' : '#fff',
            border: `1px solid ${pushSubscribed ? '#86efac' : '#e5e7eb'}`,
            borderRadius: 12,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827', marginBottom: 2 }}>
                {pushSubscribed
                  ? tx('🔔 Notifications on', '🔔 Notificaciones activas', lang)
                  : tx('🔔 Get order updates', '🔔 Recibe actualizaciones', lang)}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                {pushSubscribed
                  ? tx("We'll notify you when your order ships or is ready.", 'Te avisaremos cuando tu pedido esté listo o sea enviado.', lang)
                  : tx('Get notified when your order is confirmed, ready, or shipped.', 'Recibe alertas cuando tu pedido sea confirmado, listo o enviado.', lang)}
              </div>
              {pushError && (
                <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>{pushError}</div>
              )}
            </div>
            <button
              onClick={pushSubscribed ? handlePushUnsubscribe : handlePushSubscribe}
              disabled={pushBusy}
              style={{
                flexShrink: 0,
                padding: '7px 16px',
                borderRadius: 8,
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: pushBusy ? 'not-allowed' : 'pointer',
                border: pushSubscribed ? '1.5px solid #86efac' : '1.5px solid #3b82f6',
                background: pushSubscribed ? '#dcfce7' : '#3b82f6',
                color: pushSubscribed ? '#166534' : '#fff',
                opacity: pushBusy ? 0.6 : 1,
              }}>
              {pushBusy
                ? '…'
                : pushSubscribed
                  ? tx('Turn off', 'Desactivar', lang)
                  : tx('Enable', 'Activar', lang)}
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
